import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Layers, ChevronRight, Shield, Zap, Check, Lock, Eye, EyeOff,
  Radio, ShieldCheck, Unplug, KeyRound, ServerCrash, CircleDot,
  ArrowRight, Loader2, AlertTriangle, CheckCircle2, XCircle,
  BarChart3, Activity, TrendingUp, Clock, ChevronDown, ExternalLink, BookOpen, Home
} from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";

const CLIP_8 = "polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0% calc(100% - 8px), 0% 8px)";

const ACTIVE_PLATFORMS = [
  { id: "demo", label: "Demo Mode", desc: "Live mock data — no API keys needed", icon: Zap, color: "#38BDF8", needsKeys: false },
  { id: "binance", label: "Binance Futures", desc: "Crypto futures — read-only or trade", icon: Layers, color: "#F0B90B", needsKeys: true },
  { id: "zerodha", label: "Zerodha (Kite)", desc: "Indian equities & F&O via Kite Connect", icon: BarChart3, color: "#387ED1", needsKeys: true },
  { id: "alpaca", label: "Alpaca", desc: "US equities — commission-free API trading", icon: TrendingUp, color: "#F2C94C", needsKeys: true },
  { id: "mt5", label: "MetaTrader 5", desc: "Forex, CFDs, and indices via MT5", icon: Activity, color: "#2563EB", needsKeys: true },
];

const COMING_SOON_PLATFORMS = [
  { id: "upstox", label: "Upstox", color: "#8B5CF6" },
  { id: "angelone", label: "Angel One", color: "#EF4444" },
  { id: "ibkr", label: "Interactive Brokers", color: "#D62B2B" },
  { id: "tradier", label: "Tradier", color: "#22C55E" },
];

const SETUP_GUIDES = {
  binance: {
    url: "binance.com",
    steps: [
      "Log in to Binance → go to API Management",
      "Click Create API → choose System Generated",
      'Label it "ACE OS" → complete verification',
      "Enable Futures only — disable Spot & Withdrawals",
      "Restrict to your IP address (recommended)",
      "Copy API Key and Secret below",
    ],
    note: "Only enable Futures permissions. Never enable withdrawals.",
  },
  zerodha: {
    url: "kite.trade",
    steps: [
      "Go to kite.trade → log in with your credentials",
      "Navigate to My Profile → API",
      'Click Create new app → name it "ACE OS"',
      "Set Redirect URL to any valid URL",
      "Copy your API Key and API Secret below",
    ],
    note: "Kite Connect provides read-only market data by default.",
  },
  alpaca: {
    url: "alpaca.markets",
    steps: [
      "Sign up or log in at alpaca.markets",
      "Go to Paper Trading (recommended for testing)",
      "Navigate to API Keys in the sidebar",
      "Click Generate New Key",
      "Copy Key ID and Secret Key below",
    ],
    note: "Start with Paper Trading to test safely with virtual funds.",
  },
  mt5: {
    url: "metatrader5.com",
    steps: [
      "Open your MT5 terminal or broker dashboard",
      "Go to Tools → Options → Expert Advisors",
      "Enable Allow WebRequest for listed URL",
      "Find your account credentials from your broker",
      "Enter your MT5 Login ID and Password below",
    ],
    note: "Credentials depend on your MT5 broker. Contact them for API access.",
  },
};

const TRUST_ITEMS = [
  { icon: Lock, label: "Funds remain on exchange", detail: "ACE OS never holds or moves your funds" },
  { icon: ShieldCheck, label: "No custody", detail: "Your assets stay in your exchange wallet" },
  { icon: Unplug, label: "No withdrawal access", detail: "We never request withdrawal permissions" },
  { icon: KeyRound, label: "API keys encrypted", detail: "AES-256 encryption at rest" },
  { icon: ServerCrash, label: "Disconnect anytime", detail: "Revoke access instantly from settings" },
];

const labelStyle = {
  display: "block", fontFamily: "'Inter Tight', sans-serif", fontSize: "9px",
  fontWeight: "600", letterSpacing: "0.16em", color: "#475569",
  textTransform: "uppercase", marginBottom: "6px",
};

// ─── Setup Guide — collapsible per-platform walkthrough ────────────
function SetupGuide({ platformId, color }) {
  const [open, setOpen] = useState(false);
  const guide = SETUP_GUIDES[platformId];
  if (!guide) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.15 }}
      className="mb-5"
      data-testid="setup-guide"
    >
      <button
        onClick={() => setOpen(!open)}
        data-testid="setup-guide-toggle"
        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 transition-all duration-200"
        style={{
          clipPath: CLIP_8,
          background: open ? `${color}08` : "rgba(255,255,255,0.02)",
          border: `1px solid ${open ? `${color}18` : "rgba(255,255,255,0.04)"}`,
        }}
      >
        <BookOpen size={12} style={{ color: color || "#475569", flexShrink: 0 }} />
        <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", fontWeight: "600", color: "#94A3B8", letterSpacing: "0.06em", flex: 1, textAlign: "left" }}>
          How to get your API keys
        </span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={12} style={{ color: "#475569" }} />
        </motion.div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-3.5 pt-3 pb-1" style={{ borderLeft: `1px solid ${color}15`, marginLeft: "14px", marginTop: "4px" }}>
              {/* Steps */}
              <div className="space-y-2.5 mb-3">
                {guide.steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-2.5" data-testid={`setup-step-${i}`}>
                    <span style={{
                      fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", fontWeight: "700",
                      color, minWidth: "14px", lineHeight: "16px",
                    }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#94A3B8", lineHeight: "16px" }}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>

              {/* Note */}
              {guide.note && (
                <div className="flex items-start gap-2 px-2.5 py-2 mb-2" style={{ clipPath: CLIP_8, background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.1)" }}>
                  <AlertTriangle size={10} style={{ color: "#F59E0B", flexShrink: 0, marginTop: "1px" }} />
                  <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#F59E0B", lineHeight: 1.4 }}>
                    {guide.note}
                  </span>
                </div>
              )}

              {/* Link to platform */}
              <a
                href={`https://${guide.url}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="setup-guide-link"
                className="inline-flex items-center gap-1.5 mt-1 mb-2 transition-colors hover:text-[#E2E8F0]"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#475569", letterSpacing: "0.04em" }}
              >
                <ExternalLink size={9} />
                Open {guide.url}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Live Validation Check Item ────────────────────────────────────
function ValidationCheck({ check, index }) {
  const statusIcon = {
    pass: <CheckCircle2 size={13} style={{ color: "#22C55E" }} />,
    fail: <XCircle size={13} style={{ color: "#EF4444" }} />,
    pending: <Loader2 size={13} style={{ color: "#475569" }} className="animate-spin" />,
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.15 }}
      className="flex items-center gap-2.5 py-1.5"
      data-testid={`validation-check-${check.id}`}
    >
      {statusIcon[check.status] || statusIcon.pending}
      <span style={{
        fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px",
        color: check.status === "pass" ? "#94A3B8" : check.status === "fail" ? "#EF4444" : "#475569",
      }}>
        {check.label}
      </span>
      {check.message && (
        <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#EF4444", marginLeft: "auto" }}>
          {check.message}
        </span>
      )}
    </motion.div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", api_key: "", api_secret: "", mode: "read_only", testnet: false });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState("");
  const [liveChecks, setLiveChecks] = useState([]);

  const selectedPlatform = ACTIVE_PLATFORMS.find(p => p.id === selected);
  const needsKeys = selectedPlatform?.needsKeys ?? false;

  // Live validation as user types keys
  useEffect(() => {
    if (!needsKeys) { setLiveChecks([]); return; }
    const checks = [];
    if (form.api_key.length > 0) {
      checks.push({
        id: "format",
        label: "Key format valid",
        status: form.api_key.length >= 10 ? "pass" : "pending",
      });
    }
    if (form.api_secret.length > 0) {
      checks.push({
        id: "secret",
        label: "Secret format valid",
        status: form.api_secret.length >= 10 ? "pass" : "pending",
      });
    }
    if (form.api_key.length >= 10 && form.api_secret.length >= 10) {
      checks.push({ id: "withdrawal", label: "No withdrawal access detected", status: "pending" });
      checks.push({ id: "connection", label: "Secure connection", status: "pending" });
    }
    setLiveChecks(checks);
  }, [form.api_key, form.api_secret, selected]);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError("");
    try {
      const { data } = await axios.post(`${API}/platforms/test-connection`, {
        connector_type: selected,
        api_key: form.api_key,
        api_secret: form.api_secret,
        testnet: form.testnet,
      });
      setTestResult(data);
      setLiveChecks(data.checks || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async () => {
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/platforms/connect`, {
        name: form.name || `${selectedPlatform?.label || "Platform"} #1`,
        connector_type: selected,
        mode: form.mode,
        api_key: form.api_key,
        api_secret: form.api_secret,
        testnet: form.testnet,
      });
      navigate("/");
    } catch (e) {
      setError(e.response?.data?.detail || "Connection failed");
    } finally {
      setLoading(false);
    }
  };

  const canConnect = !needsKeys || (form.api_key.length >= 10 && form.api_secret.length >= 10);

  return (
    <div className="min-h-screen flex" style={{ background: "#0B0D10" }} data-testid="onboarding-page">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <div className="absolute inset-0 ace-bg-radial" />
        <div className="ace-noise" />
      </div>

      {/* Home button */}
      <Link to="/" data-testid="onboarding-home-button" className="fixed top-6 left-6 z-50 flex items-center gap-2 px-3 py-2 transition-all hover:bg-white/[0.03]"
        style={{ clipPath: CLIP_8, border: "1px solid rgba(255,255,255,0.06)" }}>
        <Home size={14} style={{ color: "#94A3B8" }} />
        <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", fontWeight: "600", color: "#94A3B8", letterSpacing: "0.08em" }}>HOME</span>
      </Link>

      {step === 1 && (
        <div className="flex-1 flex items-center justify-center p-8 relative z-10">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-1.5 bg-[#2563EB] rounded-full" />
              <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", color: "#475569", letterSpacing: "0.16em", fontWeight: "600" }}>SETUP</span>
            </div>
            <h1 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", fontSize: "24px", marginBottom: "8px" }}>
              Connect Your Platform
            </h1>
            <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#64748B", fontSize: "13px", marginBottom: "28px" }}>
              Start with read-only access. Upgrade permissions anytime.
            </p>

            {/* Active platforms */}
            <div className="space-y-2.5 mb-5">
              {ACTIVE_PLATFORMS.map((pt) => {
                const Icon = pt.icon;
                return (
                  <button key={pt.id} onClick={() => setSelected(pt.id)} data-testid={`platform-type-${pt.id}`}
                    className="w-full text-left p-4 transition-all duration-200"
                    style={{
                      clipPath: CLIP_8,
                      background: selected === pt.id ? "rgba(37,99,235,0.06)" : "#111318",
                      outline: selected === pt.id ? "1px solid rgba(37,99,235,0.25)" : "1px solid rgba(255,255,255,0.04)",
                    }}>
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 flex items-center justify-center" style={{ clipPath: CLIP_8, background: `${pt.color}12` }}>
                        <Icon size={16} style={{ color: pt.color }} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <div style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "13px" }}>{pt.label}</div>
                        <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#475569", fontSize: "11px", marginTop: "2px" }}>{pt.desc}</div>
                      </div>
                      {selected === pt.id && <Check size={15} className="text-[#2563EB]" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Coming Soon platforms */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={10} style={{ color: "#334155" }} />
                <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", fontWeight: "600", letterSpacing: "0.16em", color: "#334155" }}>
                  COMING NEXT
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {COMING_SOON_PLATFORMS.map((pt) => (
                  <div key={pt.id} data-testid={`platform-coming-${pt.id}`}
                    className="flex items-center gap-2 px-3 py-1.5"
                    style={{ clipPath: CLIP_8, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)" }}>
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: pt.color, opacity: 0.4 }} />
                    <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", color: "#475569", fontWeight: "500" }}>{pt.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={() => selected && setStep(2)} disabled={!selected} data-testid="onboarding-next-btn"
              className="w-full text-white py-3 transition-all duration-200 disabled:opacity-40"
              style={{ clipPath: CLIP_8, background: "#2563EB", fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", letterSpacing: "0.12em" }}>
              CONTINUE <ChevronRight size={13} className="inline" />
            </button>
            <button onClick={() => navigate("/")} data-testid="onboarding-skip-btn"
              className="w-full py-2 mt-2 transition-colors"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "12px", color: "#475569" }}>
              Skip for now
            </button>
          </motion.div>
        </div>
      )}

      {step === 2 && (
        <div className="flex-1 flex relative z-10">

          {/* ═══ LEFT: Safety Messaging ═══ */}
          <div className="hidden xl:flex w-[280px] flex-col justify-center px-10" style={{ borderRight: "1px solid rgba(255,255,255,0.03)" }}>
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <div className="flex items-center gap-2 mb-5">
                <Shield size={14} style={{ color: "#2563EB" }} />
                <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", fontWeight: "600", color: "#2563EB", letterSpacing: "0.16em" }}>
                  SECURITY
                </span>
              </div>
              <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", fontSize: "18px", lineHeight: 1.3, marginBottom: "16px" }}>
                Your keys.<br />Your control.
              </h3>
              <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "12px", color: "#475569", lineHeight: 1.6, marginBottom: "28px" }}>
                ACE OS connects to your exchange via read-only API. We never access, hold, or move your funds.
              </p>

              {/* Visual trust indicator */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div style={{ width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center", clipPath: CLIP_8, background: "rgba(34,197,94,0.06)" }}>
                    <Lock size={12} style={{ color: "#22C55E" }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", fontWeight: "600", color: "#94A3B8" }}>AES-256 Encryption</div>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "9px", color: "#334155" }}>Keys encrypted at rest</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div style={{ width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center", clipPath: CLIP_8, background: "rgba(34,197,94,0.06)" }}>
                    <Unplug size={12} style={{ color: "#22C55E" }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", fontWeight: "600", color: "#94A3B8" }}>Instant Disconnect</div>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "9px", color: "#334155" }}>Revoke access anytime</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div style={{ width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center", clipPath: CLIP_8, background: "rgba(34,197,94,0.06)" }}>
                    <Eye size={12} style={{ color: "#22C55E" }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", fontWeight: "600", color: "#94A3B8" }}>Read-Only Default</div>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "9px", color: "#334155" }}>No trade actions by default</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* ═══ CENTER: Input Form ═══ */}
          <div className="flex-1 flex items-center justify-center px-8 py-10">
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">

              {/* Header */}
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: ACTIVE_PLATFORMS.find(p => p.id === selected)?.color || "#38BDF8" }} />
                <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", color: "#475569", letterSpacing: "0.16em", fontWeight: "600" }}>
                  {(ACTIVE_PLATFORMS.find(p => p.id === selected)?.label || "PLATFORM").toUpperCase()}
                </span>
              </div>
              <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", fontSize: "22px", marginBottom: "6px" }}>
                Connect Your Platform
              </h2>
              <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#64748B", fontSize: "12px", marginBottom: "20px" }}>
                Start with read-only access. Upgrade permissions anytime.
              </p>

              {/* Setup Guide — per-platform walkthrough */}
              {needsKeys && <SetupGuide platformId={selected} color={selectedPlatform?.color} />}

              {/* Access Mode */}
              <div className="mb-5">
                <label style={labelStyle}>ACCESS MODE</label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setForm({ ...form, mode: "read_only" })} data-testid="mode-read_only"
                    className="p-3 text-left transition-all duration-200 relative"
                    style={{
                      clipPath: CLIP_8,
                      background: form.mode === "read_only" ? "rgba(34,197,94,0.04)" : "rgba(255,255,255,0.02)",
                      border: `1px solid ${form.mode === "read_only" ? "rgba(34,197,94,0.2)" : "rgba(255,255,255,0.05)"}`,
                    }}>
                    <div className="flex items-center gap-2 mb-1">
                      <CircleDot size={12} style={{ color: form.mode === "read_only" ? "#22C55E" : "#334155" }} />
                      <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", color: form.mode === "read_only" ? "#E2E8F0" : "#64748B" }}>
                        Read-Only
                      </span>
                      {form.mode === "read_only" && (
                        <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "7px", fontWeight: "700", letterSpacing: "0.12em", color: "#22C55E", marginLeft: "auto" }}>
                          RECOMMENDED
                        </span>
                      )}
                    </div>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#475569", paddingLeft: "20px" }}>
                      Monitor positions and risk. No trading or withdrawals.
                    </div>
                  </button>
                  <button onClick={() => setForm({ ...form, mode: "trade_enabled" })} data-testid="mode-trade_enabled"
                    className="p-3 text-left transition-all duration-200"
                    style={{
                      clipPath: CLIP_8,
                      background: form.mode === "trade_enabled" ? "rgba(245,158,11,0.04)" : "rgba(255,255,255,0.02)",
                      border: `1px solid ${form.mode === "trade_enabled" ? "rgba(245,158,11,0.2)" : "rgba(255,255,255,0.05)"}`,
                    }}>
                    <div className="flex items-center gap-2 mb-1">
                      <CircleDot size={12} style={{ color: form.mode === "trade_enabled" ? "#F59E0B" : "#334155" }} />
                      <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", color: form.mode === "trade_enabled" ? "#E2E8F0" : "#64748B" }}>
                        Trade Enabled
                      </span>
                    </div>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#475569", paddingLeft: "20px" }}>
                      Execute trades. Advanced users only.
                    </div>
                  </button>
                </div>
              </div>

              {/* Platform Name */}
              <div className="mb-4">
                <label style={labelStyle}>PLATFORM NAME</label>
                <input type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={selected === "demo" ? "Demo Platform" : "e.g. Binance Main"}
                  data-testid="platform-name-input"
                  className="w-full bg-white/[0.03] border border-white/[0.06] text-[#E2E8F0] text-sm px-3 py-2.5 placeholder-[#334155] focus:border-[#2563EB]/30 focus:outline-none transition-all"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif", clipPath: CLIP_8 }}
                />
              </div>

              {/* API Key fields — for platforms that need keys */}
              {needsKeys && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-4 mb-4">
                  <div>
                    <label style={labelStyle}>API KEY</label>
                    <input type="text" value={form.api_key}
                      onChange={(e) => { setForm({ ...form, api_key: e.target.value }); setTestResult(null); }}
                      placeholder={`Enter your ${selectedPlatform?.label || "platform"} API key`}
                      data-testid="api-key-input"
                      className="w-full bg-white/[0.03] border border-white/[0.06] text-[#E2E8F0] text-sm px-3 py-2.5 placeholder-[#334155] focus:border-[#2563EB]/30 focus:outline-none transition-all"
                      style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", clipPath: CLIP_8 }}
                    />
                  </div>
                  <div className="relative">
                    <label style={labelStyle}>API SECRET</label>
                    <input type={showSecret ? "text" : "password"} value={form.api_secret}
                      onChange={(e) => { setForm({ ...form, api_secret: e.target.value }); setTestResult(null); }}
                      placeholder={`Enter your ${selectedPlatform?.label || "platform"} API secret`}
                      data-testid="api-secret-input"
                      className="w-full bg-white/[0.03] border border-white/[0.06] text-[#E2E8F0] text-sm px-3 py-2.5 pr-10 placeholder-[#334155] focus:border-[#2563EB]/30 focus:outline-none transition-all"
                      style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", clipPath: CLIP_8 }}
                    />
                    <button onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-3 top-[28px] text-[#475569] hover:text-[#94A3B8] transition-colors"
                      data-testid="toggle-secret-btn">
                      {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>

                  {/* Testnet toggle — Binance and Alpaca */}
                  {(selected === "binance" || selected === "alpaca") && (
                    <div className="flex items-center gap-3 px-3 py-2.5"
                      style={{ clipPath: CLIP_8, background: form.testnet ? "rgba(245,158,11,0.04)" : "rgba(255,255,255,0.015)", border: `1px solid ${form.testnet ? "rgba(245,158,11,0.15)" : "rgba(255,255,255,0.03)"}` }}
                      data-testid="testnet-toggle-container">
                      <button
                        onClick={() => setForm({ ...form, testnet: !form.testnet })}
                        data-testid="testnet-toggle-btn"
                        className="relative w-8 h-[18px] rounded-full transition-colors duration-200"
                        style={{ background: form.testnet ? "#F59E0B" : "rgba(255,255,255,0.08)" }}
                      >
                        <div className="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-all duration-200"
                          style={{ left: form.testnet ? "16px" : "2px" }} />
                      </button>
                      <div className="flex-1">
                        <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "11px", fontWeight: "600", color: form.testnet ? "#F59E0B" : "#64748B" }}>
                          {selected === "binance" ? "Testnet Mode" : "Paper Trading"}
                        </span>
                        <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "9px", color: "#475569", display: "block", marginTop: "1px" }}>
                          {form.testnet
                            ? (selected === "binance" ? "Using Binance Futures Testnet — virtual funds only" : "Using Alpaca Paper Trading — virtual funds only")
                            : "Production — real funds"}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Live validation checks */}
                  {liveChecks.length > 0 && (
                    <div className="px-3 py-2" style={{ clipPath: CLIP_8, background: "rgba(255,255,255,0.015)", border: "1px solid rgba(255,255,255,0.03)" }}
                      data-testid="live-validation-panel">
                      {liveChecks.map((c, i) => <ValidationCheck key={c.id} check={c} index={i} />)}
                    </div>
                  )}

                  {/* Test Connection button */}
                  <button onClick={handleTestConnection}
                    disabled={testing || form.api_key.length < 10 || form.api_secret.length < 10}
                    data-testid="test-connection-btn"
                    className="w-full py-2.5 flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-30"
                    style={{
                      clipPath: CLIP_8, border: "1px solid rgba(255,255,255,0.06)",
                      fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", fontWeight: "600",
                      letterSpacing: "0.12em", color: "#94A3B8",
                    }}>
                    {testing ? <Loader2 size={12} className="animate-spin" /> : <Radio size={12} />}
                    {testing ? "TESTING..." : "TEST API CONNECTION"}
                  </button>

                  {testResult?.success && (
                    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 px-3 py-2"
                      style={{ clipPath: CLIP_8, background: "rgba(34,197,94,0.04)", border: "1px solid rgba(34,197,94,0.12)" }}
                      data-testid="test-success-msg">
                      <CheckCircle2 size={12} style={{ color: "#22C55E" }} />
                      <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#22C55E" }}>
                        Connection verified — ready to connect
                      </span>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* Error */}
              {error && (
                <div data-testid="connect-error" className="mb-4 px-3 py-2.5 flex items-center gap-2"
                  style={{ clipPath: CLIP_8, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)" }}>
                  <AlertTriangle size={12} style={{ color: "#EF4444" }} />
                  <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#EF4444" }}>{error}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-2">
                <button onClick={() => { setStep(1); setTestResult(null); setLiveChecks([]); }}
                  className="py-2.5 px-5 transition-colors"
                  style={{ clipPath: CLIP_8, border: "1px solid rgba(255,255,255,0.06)", color: "#475569", fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "12px" }}>
                  Back
                </button>
                <button onClick={handleConnect} disabled={loading || !canConnect}
                  data-testid="connect-platform-btn"
                  className="flex-1 py-2.5 text-white transition-all duration-200 disabled:opacity-40 flex items-center justify-center gap-2 hero-cta-primary relative overflow-hidden"
                  style={{
                    clipPath: CLIP_8, background: "rgba(37,99,235,0.85)",
                    fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", letterSpacing: "0.12em",
                  }}>
                  <span className="relative z-10 flex items-center gap-2">
                    {loading ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />}
                    {loading ? "CONNECTING..." : "CONNECT SECURELY"}
                  </span>
                </button>
              </div>

              {/* Trust micro-strip below CTA */}
              <div className="mt-3 flex items-center justify-center gap-3" data-testid="cta-trust-strip">
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: "#334155", letterSpacing: "0.06em" }}>
                  No withdrawals
                </span>
                <span style={{ color: "#1E293B", fontSize: "8px" }}>·</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: "#334155", letterSpacing: "0.06em" }}>
                  Read-only
                </span>
                <span style={{ color: "#1E293B", fontSize: "8px" }}>·</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: "#334155", letterSpacing: "0.06em" }}>
                  Fully revocable
                </span>
              </div>
            </motion.div>
          </div>

          {/* ═══ RIGHT: Trust Panel ═══ */}
          <div className="hidden lg:flex w-[300px] flex-col justify-center px-8" style={{ borderLeft: "1px solid rgba(255,255,255,0.03)" }}>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
              <div className="flex items-center gap-2 mb-5">
                <ShieldCheck size={14} style={{ color: "#22C55E" }} />
                <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", fontWeight: "600", color: "#22C55E", letterSpacing: "0.16em" }}>
                  TRUST
                </span>
              </div>

              <div className="space-y-5">
                {TRUST_ITEMS.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + i * 0.08 }}
                      className="flex items-start gap-3"
                      data-testid={`trust-item-${i}`}>
                      <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: "#22C55E", marginTop: "5px", flexShrink: 0, boxShadow: "0 0 4px rgba(34,197,94,0.2)" }} />
                      <div>
                        <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "11px", fontWeight: "600", color: "#CBD5E1", marginBottom: "2px" }}>
                          {item.label}
                        </div>
                        <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#475569", lineHeight: 1.5 }}>
                          {item.detail}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Security badge */}
              <div className="mt-8 px-4 py-3" style={{ clipPath: CLIP_8, background: "rgba(34,197,94,0.03)", border: "1px solid rgba(34,197,94,0.08)" }}>
                <div className="flex items-center gap-2">
                  <Lock size={11} style={{ color: "#22C55E" }} />
                  <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", fontWeight: "600", color: "#22C55E", letterSpacing: "0.1em" }}>
                    ENCRYPTED CONNECTION
                  </span>
                </div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: "#334155", marginTop: "4px" }}>
                  TLS 1.3 · AES-256 · Zero-knowledge
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
}
